import React, { useEffect, useState } from 'react';
import { Activity, Cpu, Database, Monitor, Shield, Signal, Wifi, Mic } from 'lucide-react';
import StatusPanel from '../components/voice/StatusPanel';
import SystemMetric from '../components/voice/SystemMetric';
import HolographicElement from '../components/voice/HolographicElement';
import { supabase } from '../lib/supabase';

const VoiceAgent: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [cpuUsage, setCpuUsage] = useState(0);
  const [memoryUsage, setMemoryUsage] = useState(0);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [userWhatsapp, setUserWhatsapp] = useState('');

  // Send user context to N8N webhook when conversation starts
  const sendUserContextToWebhook = async (email: string, userId: string, whatsapp: string) => {
    try {
      const payload = {
        email,
        user_id: userId,
        whatsapp: whatsapp || null,
        event: 'conversation_started',
        timestamp: new Date().toISOString(),
        agent_id: import.meta.env.VITE_ELEVENLABS_AGENT_ID || "agent_5401k7d2q7k8et8t67agrc1j14x7"
      };

      console.log('📤 Enviando contexto do usuário para webhook...', payload);

      const response = await fetch('https://nwh.infinityai.com.br/webhook/GranaEmDiaVoiceAgent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        console.log('✅ Contexto do usuário enviado com sucesso para N8N');
      } else {
        console.warn('⚠️ Webhook retornou status:', response.status);
      }
    } catch (err) {
      console.error('❌ Erro ao enviar contexto do usuário:', err);
    }
  };

  // Setup widget event listeners
  const setupWidgetListeners = (email: string, userId: string, whatsapp: string) => {
    const checkWidget = setInterval(() => {
      const widget = document.querySelector('elevenlabs-convai');
      if (widget) {
        clearInterval(checkWidget);
        
        // Listener para quando a conversa começar
        widget.addEventListener('conversationStarted' as any, () => {
          console.log('🎤 Conversa iniciada, enviando contexto do usuário...');
          sendUserContextToWebhook(email, userId, whatsapp);
        });

        // Listener para quando o widget carregar
        widget.addEventListener('widgetLoaded' as any, () => {
          console.log('✅ Widget ElevenLabs carregado e pronto');
        });

        // Listener para erros
        widget.addEventListener('error' as any, (event: any) => {
          console.error('❌ Erro no widget ElevenLabs:', event);
        });
      }
    }, 100);

    // Cleanup timeout após 10 segundos
    setTimeout(() => clearInterval(checkWidget), 10000);
  };

  useEffect(() => {
    // Load ElevenLabs Convai widget script if not already loaded
    if (!document.querySelector('script[src*="elevenlabs"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@elevenlabs/convai-widget-embed';
      script.async = true;
      document.head.appendChild(script);
    }

    // Fetch user data from Supabase
    const fetchUserData = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        if (data?.user) {
          const email = data.user.email || '';
          const uuid = data.user.id || '';
          
          // Buscar WhatsApp do perfil
          const { data: profileData } = await supabase
            .from('profiles')
            .select('whatsapp')
            .eq('user_id', uuid)
            .single();
          
          const whatsapp = profileData?.whatsapp || '';
          
          console.log('✅ Email extraído (VoiceAgent):', email);
          console.log('✅ UUID extraído (VoiceAgent):', uuid);
          console.log('✅ WhatsApp extraído (VoiceAgent):', whatsapp);
          
          setUserEmail(email);
          setUserId(uuid);
          setUserWhatsapp(whatsapp);

          // Setup widget listeners once user data is loaded
          setupWidgetListeners(email, uuid, whatsapp);
        }
      } catch (err) {
        console.error('❌ Erro ao extrair dados do Supabase (VoiceAgent):', err);
      }
    };
    fetchUserData();

    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const metricsInterval = setInterval(() => {
      setCpuUsage(Math.random() * 100);
      setMemoryUsage(Math.random() * 100);
    }, 2000);

    return () => {
      clearInterval(timeInterval);
      clearInterval(metricsInterval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.1)_0%,transparent_50%)]" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(6,182,212,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6,182,212,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Top Status Bar */}
      <div className="relative z-10 flex justify-between items-center p-6 border-b border-cyan-500/20">
        <div className="flex items-center space-x-6">
          <div className="text-cyan-400 font-bold text-xl flex items-center gap-2">
            <img 
              src="https://i.postimg.cc/SK6PNf6P/Assistente-financeiro-(150-x-150-px).png" 
              alt="" 
              className="w-7 h-7 rounded-md"
            />
            <Mic className="w-6 h-6" />
            Bia da Grana em Dia - Assistente Financeiro
          </div>
          <div className="text-cyan-300 text-sm">
            {currentTime.toLocaleDateString('pt-BR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>
        <div className="flex items-center space-x-4 text-sm text-cyan-300">
          <div className="flex items-center space-x-2">
            <Signal className="w-4 h-4 text-green-400" />
            <span>ONLINE</span>
          </div>
          {userEmail && (
            <div className="flex items-center space-x-2 text-xs">
              <Shield className="w-4 h-4 text-cyan-400" />
              <span>{userEmail}</span>
            </div>
          )}
          <div className="text-cyan-400 font-mono">
            {currentTime.toLocaleTimeString('pt-BR')}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 flex-1 min-h-0 pt-8 lg:pt-16">
        {/* Left Panel */}
        <div className="lg:col-span-3 space-y-4 flex flex-col justify-center">
          <StatusPanel 
            title="Sistema"
            icon={<Monitor className="w-5 h-5" />}
            metrics={[
              { label: 'CPU', value: `${cpuUsage.toFixed(1)}%`, color: 'cyan' },
              { label: 'RAM', value: `${memoryUsage.toFixed(1)}%`, color: 'green' },
              { label: 'Conexão', value: 'Estável', color: 'blue' }
            ]}
          />
          <StatusPanel 
            title="Segurança"
            icon={<Shield className="w-5 h-5" />}
            metrics={[
              { label: 'Status', value: 'ATIVO', color: 'green' },
              { label: 'Autenticado', value: 'SIM', color: 'cyan' },
              { label: 'Criptografia', value: 'AES-256', color: 'blue' }
            ]}
          />
        </div>

        {/* Center - Voice Widget */}
        <div className="lg:col-span-6 flex items-center justify-center relative min-h-0 mt-4 lg:mt-8">
          <div className="relative">
            {/* Outer Rings */}
            <div className="absolute inset-0 -m-24 pointer-events-none">
              <HolographicElement size="lg" />
            </div>
            <div className="absolute inset-0 -m-16 pointer-events-none">
              <HolographicElement size="md" delay={1} />
            </div>
            <div className="absolute inset-0 -m-8 pointer-events-none">
              <HolographicElement size="sm" delay={2} />
            </div>
            
            {/* Central Widget Container */}
            <div className="relative z-10 w-80 h-80 lg:w-96 lg:h-96 rounded-full bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm border border-cyan-400/20 flex items-center justify-center shadow-2xl">
              <div className="w-64 h-64 lg:w-80 lg:h-80 rounded-full bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-400/30 flex items-center justify-center relative">
                {/* Breathing effect */}
                <div className="absolute inset-0 rounded-full bg-cyan-400/10 animate-ping pointer-events-none" />
                
                {/* ElevenLabs Widget - Positioned in center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <elevenlabs-convai
                    agent-id={import.meta.env.VITE_ELEVENLABS_AGENT_ID || "agent_5401k7d2q7k8et8t67agrc1j14x7"}
                    email={userEmail}
                    user-id={userId}
                  />
                </div>

                {/* Central Label */}
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-center pointer-events-none">
                  <div className="text-cyan-400 font-bold text-lg">
                    Bia
                  </div>
                  <div className="text-cyan-300/70 text-xs mt-1 flex items-center justify-center gap-1">
                    <img 
                      src="https://i.postimg.cc/SK6PNf6P/Assistente-financeiro-(150-x-150-px).png" 
                      alt="" 
                      className="w-3 h-3 rounded-sm"
                    />
                    Grana em Dia
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="lg:col-span-3 space-y-4 flex flex-col justify-center">
          <StatusPanel 
            title="Conectividade"
            icon={<Wifi className="w-5 h-5" />}
            metrics={[
              { label: 'Internet', value: 'CONECTADO', color: 'green' },
              { label: 'API', value: '99.9%', color: 'cyan' },
              { label: 'Latência', value: '12ms', color: 'blue' }
            ]}
          />
          <StatusPanel 
            title="Base de Dados"
            icon={<Database className="w-5 h-5" />}
            metrics={[
              { label: 'Transações', value: 'Sync', color: 'cyan' },
              { label: 'Metas', value: 'OK', color: 'green' },
              { label: 'Backup', value: 'Ativo', color: 'blue' }
            ]}
          />
        </div>
      </div>

      {/* Bottom Metrics Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-cyan-500/20 p-3 bg-gray-900/80 backdrop-blur-sm">
        <div className="flex justify-center space-x-8">
          <SystemMetric 
            icon={<Activity className="w-4 h-4" />} 
            label="Atividade"
            value="Alta"
            color="green"
          />
          <SystemMetric 
            icon={<Cpu className="w-4 h-4" />} 
            label="Processamento"
            value={`${cpuUsage.toFixed(0)}%`}
            color="cyan"
          />
          <SystemMetric 
            icon={<Mic className="w-4 h-4" />} 
            label="Agente"
            value="Ativo"
            color="blue"
          />
        </div>
      </div>
    </div>
  );
};

export default VoiceAgent;